<template>
  <div class="main-content">
    <div id="seats-table">
      <b-table striped hover :items="seats" :fields="fields" :busy="loading">
        <template #table-busy>
          <div class="text-center text-secondary my-2">
            <b-spinner class="align-middle"></b-spinner>
          </div>
        </template>

        <template #cell(actions)="row">
          <button
            v-b-modal.seats-edit-modal
            class="btn p-0"
            @click="selectedItem = row.item"
          >
            {{ $t("dashboard.seats.edit-button") }}
          </button>
        </template>
      </b-table>
    </div>
    <SeatsEditModal :item="selectedItem" @cliked="editSeat" />
    <div class="save-button mt-5" v-if="status !== 'approved'">
      <LoadingButton
        :text="$t('utils.save')"
        :classes="['btn-primary', 'w-100']"
        :loading="loading"
        @click="goToWelcomePage()"
      />
    </div>
  </div>
</template>

<script>
import SeatsEditModal from "./SeatsEditModal.vue";
import SpaceService from "@/services/space";
import { mapGetters } from "vuex";

export default {
  components: { SeatsEditModal },
  data() {
    return {
      loading: false,
      error: "",
      busy: "",
      fields: [
        { key: "name", label: this.$t("utils.table.name") },
        { key: "people_fitting", label: this.$t("dashboard.seats.edit.people-fitting") },
        { key: "price_hour", label: this.$t("utils.table.price-hour") },
        { key: "price_day", label: this.$t("utils.table.price-day") },
        { key: "stock", label: this.$t("dashboard.seats.edit.spots") },
        { key: "actions", label: "" }
      ],
      seats: [
        {
          name: this.$t("dashboard.seats.edit.easy-chair"),
          price_hour: null,
          price_day: null,
          people_fitting: null,
          stock: null,
          description: "",
          isNew: true,
          type: "easy_chair"
        },
        {
          name: this.$t("dashboard.seats.edit.private-desk"),
          price_hour: null,
          price_day: null,
          people_fitting: null,
          stock: null,
          description: "",
          isNew: true,
          type: "private_desk"
        },
        {
          name: this.$t("dashboard.seats.edit.meeting-rooms"),
          price_hour: null,
          price_day: null,
          people_fitting: null,
          stock: null,
          description: "",
          isNew: true,
          type: "meeting_room"
        }
      ],
      selectedItem: {},
      spaceId: ""
    };
  },
  computed: {
    ...mapGetters("space", ["status"])
  },
  mounted() {
    if (!this.status) {
      this.$router.push({ name: "WelcomePage" });
    } else {
      this.$store.dispatch(
        "utils/setSectionName",
        `${this.$t("dashboard.seats.title")}`
      );
      this.getSeats();
    }
  },
  methods: {
    async editSeat(item) {
      this.loading = true;
      let setPriceTypes = [
        {
          type: 'hourly',
          value: item.price_hour === "" || item.price_hour === null ? null : +item.price_hour,
          currency: item.price_currency
         },
         {
          type: 'daily',
          value: item.price_day === "" || item.price_day === null ? null : +item.price_day,
          currency: item.price_currency
         }
      ];

      let priceTypes = setPriceTypes.filter((el) => el.value !== null);

      const body = {
        name: item.name,
        description: item.description,
        numberOfSpots: +item.stock,
        peopleFitting: +item.people_fitting,
        type: item.type,
        priceTypes: priceTypes
      }
      try {
        item.isNew
          ? await SpaceService.createSpaceProduct(this.spaceId, body)
          : await SpaceService.editSpaceProduct(this.spaceId, item.id, body);

        this.getSeats();
      } catch (error) {
        this.error = error.response.data.message;

        setTimeout(() => {
          this.error = "";
        }, 2500);
      } finally {
        this.loading = false;
        this.busy = false;
      }
    },
    async goToWelcomePage() {
      try {
          this.loading = true;

          const { data: space } = await SpaceService.getSpaceById(this.spaceId);

          this.$store.dispatch("space/setSpace", space);
        } catch (error) {
        } finally {
          this.loading = false;
          this.$router.push({name: "WelcomePage"})
        }
    },
    async getSeats() {
      this.loading = true;
      this.spaceId = this.$store.getters["currentUser/spaceId"];

      try {
        const { data } = await SpaceService.getSpaceById(this.spaceId);

        this.seats = this.mapSeats(data.products);
      } catch (error) {
        this.error = error.response.data.message;

        setTimeout(() => {
          this.error = "";
        }, 2500);
      } finally {
        this.loading = false;
        this.busy = false;
      }
    },

    mapSeats(products) {
      const mappedProduct = products.map(product => ({
        id: product.id,
        name: this.mapSeatType(product.type),
        price_hour: product.priceTypes.filter((price) => price.type === 'hourly').length !== 0 ? +product.priceTypes.filter((price) => price.type === 'hourly').map((el) => el.value) : null,
        price_day: product.priceTypes.filter((price) => price.type === 'daily').length !== 0 ? +product.priceTypes.filter((price) => price.type === 'daily').map((el) => el.value) : null,
        price_currency: product.priceTypes.map((el) => el)[0].currency,
        type: product.type,
        description: product.description,
        people_fitting: product.peopleFitting,
        stock: product.numberOfSpots
      }));
      const easyChair = mappedProduct.find(
        product => product.type === "easy_chair"
      );

      const privateDesk = mappedProduct.find(
        product => product.type === "private_desk"
      );

      const meetingRoom = mappedProduct.find(
        product => product.type === "meeting_room"
      );

      return [
        easyChair || this.seats[0],
        privateDesk || this.seats[1],
        meetingRoom || this.seats[2]
      ];
    },

    mapSeatType(seatType) {
      if (seatType === "easy_chair") {
        return this.$t("dashboard.seats.edit.easy-chair");
      } else if (seatType === "private_desk") {
        return this.$t("dashboard.seats.edit.private-desk");
      } else if (seatType === "meeting_room") {
        return this.$t("dashboard.seats.edit.meeting-rooms");
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "src/assets/scss/fonts";
@import "src/assets/scss/style";

.main-content {
  h2 {
    font-family: $mulish;
    font-style: normal;
    font-weight: bold;
    font-size: 24px;
    line-height: 30px;
    letter-spacing: 0.3px;
    color: #252733;
  }

  table {
    td {
      button {
        font-family: $mulish;
        font-style: normal;
        font-weight: bold;
        font-size: 13px;
        line-height: 16px;
        letter-spacing: 0.3px;
        color: #ff2d2d;

        &:focus {
          box-shadow: none;
        }
      }
    }
  }

  .save-button {
    width: 100px
  }
}
</style>

<style lang="scss">
@import "src/assets/scss/components/table";
</style>



// WEBPACK FOOTER //
// src/pages/seats/SeatsPage.vue